import React, { useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { T, useTranslateKey } from "../../../hooks/useTranslate";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import {
  PARTNERS_PAGE_PATH,
  resolveMemberViewable,
} from "../../../common/memberProfileAccess";
import { getCustomerCategory } from "../../../common/helper-functions";
import { useMixPanel } from "../../../hooks/useMixPanel";
import { useApi } from "../../../hooks/useApi";
import { DistributorContext } from "../../../hooks/DistributorContext";
import { useAuth } from "../../../hooks/useAuth";
import { Stack, Typography, CircularProgress, useTheme } from "@mui/material";
import { Progress } from "@unicity/design-system";
import { LanguageContext } from "../../../hooks/LanguageContext";
import { MemberContext } from "../../../hooks/MemberContext";
import {
  isAsianMarket,
  shouldShowDenominator,
  shouldHighlightLeg
} from "../../../constants/asianMarketRankQual.constants";

const RankQualBox = ({
  color1,
  color2,
  color3,
  legNumber,
  name,
  leg,
  inDrawer,
  thirdLegCount,
  selectedRankDetails,
  allLegVolumes
}) => {
  const { localizeNumber } = useContext(LanguageContext);
  const { memberId, isDistributorLevelLimit } = useContext(MemberContext);
  const { setDistributorData, distributorData } = useContext(DistributorContext);
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { logEvent } = useMixPanel();
  const { sendRequest } = useApi();
  const { showSnackbar } = useSnackbar();
  // Same key MembersDrawer's denial screen uses — no new copy (GO-3366 AC).
  const accessDeniedText = useTranslateKey("access_denied");
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isClickAllowed = !(name === undefined || !leg?.value || isDistributorLevelLimit)

  const legMetric = selectedRankDetails?.metrics?.filter(item => item.name.includes(`ov_leg${legNumber}`))[0];
  const requiredLegValue = legMetric?.requiredValue;

  // Asian market logic
  const userCountryCode = inDrawer ? distributorData?.market : userInfo?.countryCode;
  const isAsianMarketUser = isAsianMarket(userCountryCode);
  const rankCode = selectedRankDetails?.rank;
  const shouldShowDenom = isAsianMarketUser && shouldShowDenominator(
    parseInt(legNumber),
    rankCode,
    allLegVolumes
  );
  const shouldHighlight = isAsianMarketUser && shouldHighlightLeg(
    parseInt(legNumber),
    rankCode,
    allLegVolumes
  );
  // linear progress bar doesn't handle big numbers over 100%
  const calculateProgress = () => {
    // When the API marks the leg as qualified, the bar must be full.
    // This guards against partial values during active months where the per-rank
    // metric value can sit below requiredValue mid-month even though the leg is
    // already qualified — causing the bar to render at e.g. 85% instead of 100%.
    if (legMetric?.qualified === true) return 100;

    if (!leg || leg.value === undefined || requiredLegValue === undefined || requiredLegValue === null) {
      return 0;
    }
    if (leg.value === 0 && requiredLegValue === 0) return 0;
    if (requiredLegValue === 0) return 100;
    return Math.min((leg.value / requiredLegValue) * 100, 100);
  };

  const formatName = () => {
    if (JSON.stringify(name) === "{}") {
      return "-";
    } else if (name === undefined) {
      return "-";
    } else if (legNumber === "3") {
      return <>{localizeNumber(thirdLegCount.members)} <T>members</T> / {localizeNumber(thirdLegCount.partners)} <T>partners</T></>;
    } else {
      return name.humanName.fullName;
    }
  };

  const handleBoxClick = async () => {
    // if leg box is empty or already loading, do nothing
    if (!isClickAllowed || isLoading) return;

    // for leg box 1 and 2, navigate to profile based on type, for leg box 3, navigate to partners page
    if (["1", "2"].includes(legNumber)) {
      setIsLoading(true);
      try {
        // Get the customer data
        const response = await sendRequest({
          method: "get",
          addPrefix: false,
          endpoint: `${name.href}?expand=customer,profilePicture,sponsor`,
        });

        // 86e2w5hdg: categorize the same way the access gate below does, via
        // getCustomerCategory (Associate/Partner/Distributor -> "partner",
        // case-insensitively). It returns exactly the "partner"/"member"
        // spellings Drawers.jsx keys its drawer map on, so the value is
        // drop-in for both the ?drawer= param and the analytics user_type.
        const userType = getCustomerCategory(response.data.type);

        // GO-3366: don't navigate into a drawer that will refuse. On /partners*
        // MembersDrawer denies members outside the user's top two levels
        // (GO-3345 round 2); this asks the SAME rule — shared via
        // common/memberProfileAccess.js — at click time, using the record just
        // fetched (it already carries the sponsor). Elsewhere the drawer does
        // not deny, so the click stays allowed — this gate matches the drawer,
        // it does not extend the rule to new surfaces. Fails closed on lookup
        // errors, exactly like the drawer's resolved-as-denied path.
        // Decide member-vs-partner the way the drawer does — via
        // getCustomerCategory, which treats Associate/Partner/Distributor
        // (case-insensitively) as partner. The narrower `=== "Associate"`
        // test would send mis-typed partners at depth into the member rule
        // and deny them, which GO-3345 forbids. `userType` above derives
        // from the same helper (86e2w5hdg), so the drawer query param and
        // analytics agree with this gate by construction.
        if (
          getCustomerCategory(response.data.type) === "member" &&
          PARTNERS_PAGE_PATH.test(location.pathname)
        ) {
          const viewable = await resolveMemberViewable({
            targetCustomerRecord: response.data,
            userUnicityId: userInfo?.unicityId,
            sendRequest,
          });
          if (!viewable) {
            showSnackbar(accessDeniedText, "info");
            return;
          }
        }

        // Set the distributor data to trigger the drawer's data fetch
        setDistributorData(response.data);

        logEvent("open_profile", {
          location: inDrawer ? "rank_tracker_sidebar" : "rank_tracker_dashboard",
          user_type: userType,
          leg_number: legNumber
        });

        navigate(`${location.pathname}?drawer=${userType}&id=${name.id.unicity}&drawerContent=profile`, {
          state: {
            customerData: response.data,
            location: location,
            userFirstName: response.data.humanName.firstName,
            userFullName: response.data.humanName.fullName
          }
        });
      } catch (_error) {
        // Default to member if API call fails. Not a getCustomerCategory
        // site (86e2w5hdg): the lookup threw, so there is no type to
        // categorize — this is the fail-safe default, not a categorization.
        const userType = "member";
        logEvent("open_profile", {
          location: inDrawer ? "rank_tracker_sidebar" : "rank_tracker_dashboard",
          user_type: userType,
          leg_number: legNumber
        });
        navigate(`${location.pathname}?drawer=${userType}&id=${name.id.unicity}&drawerContent=profile`, {
          state: { location: location }
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      logEvent("partners_view", {
        location: inDrawer ? "rank_tracker_sidebar" : "rank_tracker_dashboard",
        user_type: "partner",
      })
      if (memberId) {
        navigate(`/partners/${memberId}`);
      } else {
        navigate(`/partners`);
      }
    }
  }

  return (
    <Stack
      sx={{
        padding: "16px",
        background: !inDrawer && "rgba(145, 158, 171, 0.05)",
        borderRadius: "8px",
        cursor: (!isClickAllowed || isLoading) ? "not-allowed" : "pointer",
        "&:hover": {
          background: "rgba(145, 158, 171, 0.1)"
        },
        position: "relative",
        // Add highlighting for Asian markets
        ...(shouldHighlight && {
          border: `2px solid ${theme.palette.primary.main}`,
          backgroundColor: theme.palette.mode === 'light'
            ? "rgba(25, 118, 210, 0.04)"
            : "rgba(144, 202, 249, 0.04)"
        })
      }}
      gap="10px"
      onClick={handleBoxClick}
      data-test-id={`leg-${legNumber}-box${shouldHighlight ? '-highlighted' : ''}`}
    >
      {isLoading && (
        <Stack
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            borderRadius: "8px"
          }}
        >
          <CircularProgress size={24} />
        </Stack>
      )}
      <Progress
        value={calculateProgress()} data-test-id={`leg-${legNumber}-progress-bar`}
        variant="linear"
        size="small"
        sx={{
          '& .MuiLinearProgress-root': {
            backgroundColor: color1,
          },
          '& .MuiLinearProgress-bar': {
            backgroundColor: `${color2} !important`,
            backgroundImage: `linear-gradient(90deg, rgba(255, 255, 255, 0.2) 45%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0.2) 55%) !important`,
            backgroundBlendMode: "overlay"
          },
        }}
      />
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap="4px">
          <Stack
            data-test-id={`leg-${legNumber}-chip`}
            justifyContent="center"
            sx={{
              backgroundColor: isDarkMode ? color3 : color1,
              color: isDarkMode ? '#FFFFFF' : color3,
              fontSize: "9px",
              fontWeight: "600",
              padding: "2px 7px",
              borderRadius: "38px",
              whiteSpace: 'nowrap'
            }}>
            <T>leg</T> {legNumber}
          </Stack>
          <Typography
            data-test-id={`leg-${legNumber}-name`}
            variant="h9"
          >
            {formatName()}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ whiteSpace: 'nowrap' }}>
          <Typography
            data-test-id={`leg-${legNumber}-value`}
            variant="h9"
          >
            {leg?.value !== undefined ? localizeNumber(leg.value) : 0}
          </Typography>
          {/* Conditional denominator display for Asian markets */}
          {(isAsianMarketUser ? shouldShowDenom : true) && (
            <Typography
              data-test-id={`leg-${legNumber}-required-value`}
              variant="h9"
              sx={{
                opacity: ".6"
              }}
            >
              &nbsp;/ {requiredLegValue ? localizeNumber(requiredLegValue) : 0}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export { RankQualBox };
